import { z } from "zod";

/** Valeurs proposées par défaut dans l'écran de paramétrage (modifiables). */
export const AI_PROVIDER_PRESETS = {
  deepseek: {
    label: "DeepSeek",
    base_url: "https://api.deepseek.com",
    text_model: "deepseek-v4-flash",
    vision_model: "deepseek-v4-flash-vision-exp",
  },
  openai: {
    label: "OpenAI",
    base_url: "https://api.openai.com/v1",
    text_model: "gpt-4o-mini",
    vision_model: "gpt-4o-mini",
  },
  custom: {
    label: "Personnalisé (compatible OpenAI)",
    base_url: "",
    text_model: "",
    vision_model: "",
  },
} as const;

const optionalModel = z
  .string()
  .trim()
  .max(128, "Le nom du modèle ne peut pas dépasser 128 caractères")
  .nullable()
  .optional();

/** Corps de la mise à jour des paramètres IA. */
export const AiSettingsUpdateSchema = z.object({
  enabled: z.boolean(),
  provider_name: z.string().trim().min(1, "Le nom du fournisseur est requis").max(64),
  base_url: z.string().trim().url("URL invalide").max(255),
  text_model: optionalModel,
  vision_model: optionalModel,
  /** Absente : la clé existante est conservée. Présente : elle remplace l'ancienne. */
  api_key: z.string().trim().min(8, "La clé API semble trop courte").max(512).optional(),
  /** Supprime la clé enregistrée (ignoré si `api_key` est fournie). */
  clear_api_key: z.boolean().optional(),
  timeout_ms: z.number().int().min(1000).max(120000),
  max_tokens: z.number().int().min(16).max(8192),
  temperature: z.number().min(0).max(2),
  /** Suggestions maximum par groupe et par mois (0 = illimité). */
  monthly_limit_per_group: z.number().int().min(0).max(1_000_000),
  /** Prix du fournisseur en € par million de jetons (estimation des coûts). Absent : valeur actuelle conservée. */
  price_input_per_million: z.number().min(0).max(10_000).optional(),
  price_output_per_million: z.number().min(0).max(10_000).optional(),
});
export type AiSettingsUpdate = z.infer<typeof AiSettingsUpdateSchema>;

/** Paramètres renvoyés au navigateur : la clé n'est jamais incluse, seulement ses 4 derniers caractères. */
export const AiSettingsResponseSchema = z.object({
  enabled: z.boolean(),
  provider_name: z.string(),
  base_url: z.string(),
  text_model: z.string().nullable(),
  vision_model: z.string().nullable(),
  has_api_key: z.boolean(),
  api_key_last4: z.string().nullable(),
  timeout_ms: z.number(),
  max_tokens: z.number(),
  temperature: z.number(),
  monthly_limit_per_group: z.number(),
  price_input_per_million: z.number(),
  price_output_per_million: z.number(),
  encryption_configured: z.boolean(),
  updated_at: z.string().nullable(),
});
export type AiSettingsResponse = z.infer<typeof AiSettingsResponseSchema>;

export const AiConnectionTestSchema = z.object({
  /** Teste aussi le modèle vision avec une image minimale. */
  test_vision: z.boolean().optional(),
});
export type AiConnectionTest = z.infer<typeof AiConnectionTestSchema>;

export const AiConnectionTestResultSchema = z.object({
  ok: z.boolean(),
  text: z.object({ ok: z.boolean(), latency_ms: z.number().nullable(), model: z.string().nullable(), error: z.string().nullable() }),
  vision: z
    .object({ ok: z.boolean(), latency_ms: z.number().nullable(), model: z.string().nullable(), error: z.string().nullable() })
    .nullable(),
});
export type AiConnectionTestResult = z.infer<typeof AiConnectionTestResultSchema>;

/** Coût estimé d'un appel (ou d'un total) en euros à partir des jetons et des prix par million. */
export function estimateAiCost(
  tokens: { prompt: number | null | undefined; completion: number | null | undefined },
  prices: { price_input_per_million: number; price_output_per_million: number },
): number {
  return ((tokens.prompt ?? 0) * prices.price_input_per_million + (tokens.completion ?? 0) * prices.price_output_per_million) / 1_000_000;
}

/** Quota mensuel applicable à un client : son plafond s'il en a un, sinon le quota global. 0 = illimité. */
export function resolveAiMonthlyLimit(clientLimit: number | null | undefined, globalLimit: number): { limit: number; source: 'client' | 'global' } {
  return clientLimit === null || clientLimit === undefined ? { limit: globalLimit, source: 'global' } : { limit: clientLimit, source: 'client' };
}
